import React from 'react';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  HourglassEmpty as DebouncingIcon,
} from '@mui/icons-material';
import { SEARCH_MIN_CHARS } from '../../../../config/constants';

/**
 * TemplateSearchFilters Component
 *
 * Displays search and category filter controls.
 *
 * @param {Object} props
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Callback when search changes
 * @param {string} props.categoryFilter - Current category filter
 * @param {Function} props.onCategoryChange - Callback when category changes
 * @param {Array} props.categories - Array of available categories
 * @param {boolean} props.isDebouncing - Whether search is debouncing
 */
function TemplateSearchFilters({
  searchQuery,
  onSearchChange,
  categoryFilter,
  onCategoryChange,
  categories,
  isDebouncing,
}) {
  const trimmedQuery = searchQuery.trim();
  const showDebouncing = isDebouncing && trimmedQuery.length > 0;

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Grid container spacing={1.5} alignItems="center">
        <Grid size={{ xs: 12, sm: 7, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search templates (min. ${SEARCH_MIN_CHARS} chars)...`}
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {showDebouncing ? (
                    <Fade in={showDebouncing}>
                      <DebouncingIcon
                        sx={{
                          fontSize: 18,
                          color: 'text.secondary',
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }}
                      />
                    </Fade>
                  ) : (
                    <SearchIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                    />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-focused': {
                  backgroundColor: 'background.paper',
                  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
                },
              },
              '& .MuiInputBase-input': {
                paddingLeft: '12px',
                py: 1,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 5, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="category-filter-label">Category</InputLabel>
            <Select
              labelId="category-filter-label"
              value={categoryFilter}
              label="Category"
              onChange={e => onCategoryChange(e.target.value)}
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 1.5,
                '& .MuiSelect-select': {
                  paddingLeft: '12px',
                  py: 1,
                },
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(category => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TemplateSearchFilters;
